/**
 * SecretActionButtons Component
 *
 * Renders action buttons for a secret with list-response capabilities.
 */

import { Button } from '@/components/ui/button';
import { Status } from '@pillar-portal/database/dist/frontend';
import { Eye, Edit, Trash2, Ban, Check, Unlink } from 'lucide-react';
import type { SecretListItem, ThirdPartySystemId } from '@pillar-portal/api-types';

export interface SecretActionButtonsCredential {
    credential_uuid: string;
    account_id: string;
    credential_type: SecretListItem['credential_type'];
    status: Status;
    applies_to_children: boolean;
    system_id: ThirdPartySystemId;
    capabilities: SecretListItem['capabilities'];
    usage_data: SecretListItem['usage_data'];
}

interface SecretActionButtonsProps {
    secret: SecretActionButtonsCredential;
    onView: () => void;
    onEdit: () => void;
    onDisable: () => void;
    onEnable: () => void;
    onDelete: () => void;
    onDisconnect: () => void;
    disconnectTitle?: string;
}

export function SecretActionButtons({
    secret,
    onView,
    onEdit,
    onDisable,
    onEnable,
    onDelete,
    onDisconnect,
    disconnectTitle = 'Disconnect this credential from selected usages',
}: SecretActionButtonsProps) {
    const isInUse: boolean = secret.usage_data.summary.has_usages;
    const isActive: boolean = secret.status === Status.ACTIVE;
    const isDeleted: boolean = secret.status === Status.DELETED;
    const canDisplay: boolean = secret.capabilities.can_display;
    const canEdit: boolean = secret.capabilities.can_edit;
    const canDelete: boolean = secret.capabilities.can_delete;

    return (
        <div className="flex gap-2 ml-4">
            {canDisplay && (
                <Button variant="outline" size="sm" onClick={onView} title="View secret details">
                    <Eye className="h-4 w-4" />
                </Button>
            )}
            {canEdit && (
                <>
                    {!isDeleted ? (
                        <Button variant="outline" size="sm" onClick={onEdit} title="Edit secret">
                            <Edit className="h-4 w-4" />
                        </Button>
                    ) : null}
                    {isActive ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onDisable}
                            title="Disable secret"
                        >
                            <Ban className="h-4 w-4" />
                        </Button>
                    ) : isDeleted ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onEnable}
                            title="Undelete secret"
                        >
                            <Check className="h-4 w-4" />
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onEnable}
                            title="Enable secret"
                            className="text-green-700 hover:text-green-800"
                        >
                            <Check className="h-4 w-4" />
                        </Button>
                    )}
                </>
            )}
            {canDelete && !isDeleted && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onDelete}
                    title="Delete secret"
                    className="text-red-600 hover:text-red-700"
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            )}
            {canEdit && isInUse && (
                <Button variant="outline" size="sm" onClick={onDisconnect} title={disconnectTitle}>
                    <Unlink className="h-4 w-4" />
                </Button>
            )}
        </div>
    );
}
